import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, FileText, MessageSquare, Eye, Wallet, GraduationCap, Zap, Settings, ShieldCheck, Reply, Trash2 } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="kt-card p-5">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint} text-white mb-3`}><Icon size={18}/></div>
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-[#0F2922] mt-1">{value}</div>
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.is_super_admin;
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [users, setUsers] = useState([]);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [tab, setTab] = useState("stats");
  const [settings, setSettings] = useState(null);
  const [comments, setComments] = useState([]);
  const [cFilterType, setCFilterType] = useState("all");
  const [cFilterStatus, setCFilterStatus] = useState("all");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const loadStats = async () => { try { const { data } = await http.get("/admin/stats"); setStats(data);} catch{} };
  const loadDonations = async () => { try { const { data } = await http.get("/admin/donations", { params: { status: filter } }); setDonations(data);} catch{} };
  const loadUsers = async () => { try { const { data } = await http.get("/admin/users"); setUsers(data);} catch{} };
  const loadSettings = async () => { try { const { data } = await http.get("/admin/donation-settings"); setSettingsForm(data);} catch{} };
  const loadComments = async () => {
    try {
      const { data } = await http.get("/admin/comments", { params: { content_type: cFilterType, status: cFilterStatus } });
      setComments(data || []);
    } catch {}
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { if (tab === "donations") loadDonations(); }, [tab, filter]); // eslint-disable-line
  useEffect(() => { if (tab === "users") loadUsers(); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "settings") loadSettings(); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "comments") loadComments(); }, [tab, cFilterType, cFilterStatus]); // eslint-disable-line

  const sendReply = async (commentId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await http.post(`/admin/comments/${commentId}/reply`, { text: replyText });
      toast.success("Balasan terkirim");
      setReplyFor(null); setReplyText(""); loadComments();
    } catch { toast.error("Gagal mengirim balasan"); }
    finally { setReplying(false); }
  };

  const deleteComment = async (commentId) => {
    if (!confirm("Hapus komentar ini beserta balasannya?")) return;
    await http.delete(`/admin/comments/${commentId}`);
    toast.success("Komentar dihapus"); loadComments();
  };

  const review = async (id, status) => {
    try {
      await http.patch(`/admin/donations/${id}`, { status });
      toast.success(status === "approved" ? "Donasi disetujui" : "Donasi ditolak");
      loadDonations(); loadStats();
    } catch { toast.error("Gagal"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        bank_name: settingsForm.bank_name,
        account_number: settingsForm.account_number,
        account_holder: settingsForm.account_holder,
        min_amount: parseInt(settingsForm.min_amount),
        auto_approve: !!settingsForm.auto_approve,
        instructions_note: settingsForm.instructions_note || "",
      };
      const { data } = await http.patch("/admin/donation-settings", payload);
      setSettingsForm(data);
      toast.success("Pengaturan donasi disimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSavingSettings(false); }
  };

  const setS = (k, v) => setSettingsForm({ ...settingsForm, [k]: v });

  return (
    <div data-testid="admin-page">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">Admin</p>
        {isSuper && <Badge className="bg-gradient-to-r from-[#D97706] to-amber-500 text-white"><ShieldCheck size={11} className="mr-1"/>Super Admin</Badge>}
      </div>
      <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1">Panel Admin</h1>
      <p className="text-slate-600 mt-2">Statistik pengunjung, verifikasi donasi, manajemen pengguna{isSuper ? " & pengaturan sistem" : ""}.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="stats" data-testid="admin-tab-stats">Statistik</TabsTrigger>
          <TabsTrigger value="donations" data-testid="admin-tab-donations">Donasi</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Pengguna</TabsTrigger>
          <TabsTrigger value="comments" data-testid="admin-tab-comments">Komentar</TabsTrigger>
          {isSuper && <TabsTrigger value="settings" data-testid="admin-tab-settings">Pengaturan Donasi</TabsTrigger>}
        </TabsList>

        <TabsContent value="stats" className="mt-5">
          {!stats && <div className="text-slate-400">Memuat…</div>}
          {stats && (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Users} label="Pengguna" value={stats.total_users} tint="bg-[#0F2922]" />
                <Stat icon={Eye} label="Total Kunjungan" value={stats.total_visits} tint="bg-[#D97706]" />
                <Stat icon={FileText} label="Paper Dibuat" value={stats.total_papers} tint="bg-emerald-600" />
                <Stat icon={GraduationCap} label="Ide TA Dibuat" value={stats.total_theses} tint="bg-blue-600" />
                <Stat icon={MessageSquare} label="Komentar" value={stats.total_comments} tint="bg-violet-600" />
                <Stat icon={Wallet} label="Donasi Disetujui" value={stats.donations_approved} tint="bg-teal-600" />
                <Stat icon={Wallet} label="Donasi Pending" value={stats.donations_pending} tint="bg-amber-500" />
                <Stat icon={Wallet} label="Total Terkumpul" value={"Rp " + Number(stats.donations_total_amount || 0).toLocaleString("id-ID")} tint="bg-[#0F2922]" />
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="kt-card p-5">
                  <h3 className="font-semibold text-[#0F2922] mb-3">Pengguna per Jenjang</h3>
                  {Object.entries(stats.users_by_jenjang || {}).map(([j, c]) => (
                    <div key={j} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                      <span>{j}</span><b>{c}</b>
                    </div>
                  ))}
                </div>
                <div className="kt-card p-5">
                  <h3 className="font-semibold text-[#0F2922] mb-3">Halaman Terpopuler</h3>
                  {(stats.top_paths || []).map(p => (
                    <div key={p.path} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                      <span className="truncate mono text-xs">{p.path}</span><b>{p.count}</b>
                    </div>
                  ))}
                  {(!stats.top_paths || stats.top_paths.length === 0) && <div className="text-slate-400 text-sm italic">Belum ada data</div>}
                </div>
              </div>

              <div className="kt-card p-5 mt-4">
                <h3 className="font-semibold text-[#0F2922] mb-3">Kunjungan 7 Hari Terakhir</h3>
                <div className="flex items-end gap-2 h-32">
                  {(stats.visits_last_7d || []).length === 0 && <div className="text-slate-400 text-sm italic">Belum ada kunjungan</div>}
                  {(stats.visits_last_7d || []).map(d => {
                    const max = Math.max(1, ...(stats.visits_last_7d || []).map(x => x.count));
                    const h = (d.count / max) * 100;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                        <div className="text-xs font-semibold text-[#0F2922]">{d.count}</div>
                        <div className="w-full bg-[#D97706] rounded-t" style={{ height: `${h}%`, minHeight: 4 }} />
                        <div className="text-[10px] text-slate-500">{d.date.slice(5)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="donations" className="mt-5">
          <div className="flex items-center gap-3 mb-4">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="admin-donation-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="pending">Menunggu review</SelectItem>
                <SelectItem value="approved">Disetujui</SelectItem>
                <SelectItem value="rejected">Ditolak</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            {donations.map(d => (
              <div key={d.donation_id} className="kt-card p-4 flex flex-wrap items-start gap-4" data-testid="admin-donation-row">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <b className="text-[#0F2922]">{d.sender_name}</b>
                    <Badge variant="outline" className="text-xs">{d.user_email}</Badge>
                    {d.auto_approved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" variant="outline"><Zap size={10} className="mr-1"/>Auto</Badge>}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    <b className="text-[#D97706]">Rp {Number(d.amount).toLocaleString("id-ID")}</b> · Transfer: {d.transfer_date}
                  </div>
                  {d.reference_note && <div className="text-xs text-slate-500 mt-1">Catatan: {d.reference_note}</div>}
                  <div className="text-xs text-slate-400 mt-1">Kirim: {new Date(d.created_at).toLocaleString("id-ID")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={d.status === "approved" ? "bg-emerald-600" : d.status === "rejected" ? "bg-red-600" : "bg-amber-500"}>{d.status}</Badge>
                  {d.status !== "rejected" && (
                    <>
                      {d.status === "pending" && <Button size="sm" onClick={() => review(d.donation_id, "approved")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-approve-${d.donation_id}`}>Setujui</Button>}
                      <Button size="sm" variant="outline" onClick={() => review(d.donation_id, "rejected")} data-testid={`admin-reject-${d.donation_id}`}>Tolak</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {donations.length === 0 && <div className="text-slate-400 italic">Tidak ada donasi.</div>}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-5">
          <div className="kt-card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0F2922] text-[#FAFA96]">
                <tr><th className="text-left p-3">Nama</th><th className="text-left p-3">Email</th><th className="text-left p-3">Jenjang</th><th className="text-left p-3">Metode</th><th className="text-left p-3">Universitas</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-t border-slate-100">
                    <td className="p-3">{u.name}</td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3"><Badge variant="outline">{u.jenjang}</Badge></td>
                    <td className="p-3 text-slate-600">{u.auth_method}</td>
                    <td className="p-3 text-slate-600">{u.university || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && <div className="p-6 text-slate-400 italic">Memuat…</div>}
          </div>
        </TabsContent>

        <TabsContent value="comments" className="mt-5">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Select value={cFilterType} onValueChange={setCFilterType}>
              <SelectTrigger className="w-48" data-testid="admin-comment-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua konten</SelectItem>
                <SelectItem value="paper">Paper</SelectItem>
                <SelectItem value="thesis">Tugas Akhir</SelectItem>
                <SelectItem value="scholarship">Beasiswa</SelectItem>
                <SelectItem value="journal">Jurnal</SelectItem>
                <SelectItem value="haki">HAKI</SelectItem>
              </SelectContent>
            </Select>
            <Select value={cFilterStatus} onValueChange={setCFilterStatus}>
              <SelectTrigger className="w-56" data-testid="admin-comment-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua komentar</SelectItem>
                <SelectItem value="unanswered">Belum dibalas admin</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="outline">{comments.length} komentar</Badge>
          </div>
          <div className="space-y-3">
            {comments.length === 0 && <div className="text-center py-10 text-slate-400 italic">Tidak ada komentar.</div>}
            {comments.map(c => (
              <div key={c.comment_id} className="kt-card p-4" data-testid="admin-comment-row">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
                      <b className="text-[#0F2922] text-sm">{c.user_name}</b>
                      <Badge variant="outline" className="text-[10px]">{c.user_jenjang}</Badge>
                      <Badge className={`text-[10px] ${c.content_type === "paper" ? "bg-blue-600" : c.content_type === "thesis" ? "bg-emerald-600" : c.content_type === "journal" ? "bg-violet-600" : "bg-amber-500"} text-white border-0`}>{c.content_type}</Badge>
                      <span className="mono text-[10px]">{c.content_id}</span>
                      <span>· {new Date(c.created_at).toLocaleString("id-ID")}</span>
                      {c.has_admin_reply && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" variant="outline">Dibalas</Badge>}
                    </div>
                    <p className="text-sm text-slate-800 mt-2 whitespace-pre-line">{c.text}</p>
                    {(c.replies || []).map(r => (
                      <div key={r.comment_id} className="mt-2 ml-6 pl-3 border-l-2 border-emerald-300 bg-emerald-50 rounded-r-lg p-3">
                        <div className="flex items-center gap-2 text-xs">
                          <ShieldCheck size={12} className="text-emerald-600"/>
                          <b className="text-emerald-700">{r.user_name}</b>
                          <Badge className="bg-emerald-600 text-white text-[10px] border-0">Admin</Badge>
                          <span className="text-slate-500">· {new Date(r.created_at).toLocaleString("id-ID")}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{r.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setReplyFor(c.comment_id); setReplyText(""); }} data-testid={`admin-reply-${c.comment_id}`}>
                      <Reply size={13} className="mr-1"/> Balas
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => deleteComment(c.comment_id)} data-testid={`admin-del-${c.comment_id}`}>
                      <Trash2 size={14}/>
                    </Button>
                  </div>
                </div>
                {replyFor === c.comment_id && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Tulis balasan sebagai admin…" rows={3} data-testid="admin-reply-input"/>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => sendReply(c.comment_id)} disabled={replying || !replyText.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-reply-send">
                        {replying ? "Mengirim…" : "Kirim Balasan"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setReplyFor(null)}>Batal</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>


        {isSuper && (
          <TabsContent value="settings" className="mt-5">
            {!settingsForm && <div className="text-slate-400">Memuat…</div>}
            {settingsForm && (
              <div className="grid md:grid-cols-2 gap-5">
                <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-[#0F2922] via-[#173E34] to-[#0F2922] text-[#FAFA96] shadow-xl">
                  <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#D97706]/20 blur-3xl"/>
                  <div className="relative">
                    <div className="text-xs uppercase tracking-widest opacity-70">Preview Live</div>
                    <div className="text-3xl serif font-bold mt-2">{settingsForm.bank_name}</div>
                    <div className="text-2xl mono font-bold mt-4">{(settingsForm.account_number || "").replace(/(\d{4})(\d{4})(\d+)/, "$1  $2  $3")}</div>
                    <div className="mt-2 opacity-80">a.n. <b>{settingsForm.account_holder}</b></div>
                    <div className="mt-5 pt-4 border-t border-[#FAFA96]/20 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest opacity-70">Nominal Minimal</div>
                        <div className="text-xl font-bold text-[#D97706]">Rp {Number(settingsForm.min_amount || 0).toLocaleString("id-ID")}</div>
                      </div>
                      {settingsForm.auto_approve ? (
                        <Badge className="bg-emerald-500 text-white"><Zap size={10} className="mr-1"/>Auto-Approve ON</Badge>
                      ) : (
                        <Badge variant="outline" className="border-[#FAFA96]/40 text-[#FAFA96]">Review Manual</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="kt-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings size={18} className="text-[#D97706]" />
                    <h2 className="serif text-xl font-bold text-[#0F2922]">Edit Pengaturan Donasi</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nama bank</Label>
                      <Input value={settingsForm.bank_name || ""} onChange={e=>setS("bank_name", e.target.value)} data-testid="settings-bank-input" />
                    </div>
                    <div><Label>Nomor rekening</Label>
                      <Input value={settingsForm.account_number || ""} onChange={e=>setS("account_number", e.target.value)} data-testid="settings-account-input" />
                    </div>
                    <div className="col-span-2"><Label>Nama pemilik rekening</Label>
                      <Input value={settingsForm.account_holder || ""} onChange={e=>setS("account_holder", e.target.value)} data-testid="settings-holder-input" />
                    </div>
                    <div className="col-span-2"><Label>Nominal minimum (Rp)</Label>
                      <Input type="number" min={1000} step={1000} value={settingsForm.min_amount || 0} onChange={e=>setS("min_amount", parseInt(e.target.value || "0"))} data-testid="settings-min-input" />
                    </div>
                    <div className="col-span-2"><Label>Catatan instruksi (opsional)</Label>
                      <Textarea value={settingsForm.instructions_note || ""} onChange={e=>setS("instructions_note", e.target.value)} data-testid="settings-note-input" />
                    </div>
                    <div className="col-span-2 flex items-center justify-between p-4 rounded-xl bg-[#FAFA96]/50 border border-[#D97706]/20">
                      <div className="flex items-center gap-3">
                        <Zap className="text-emerald-600" />
                        <div>
                          <div className="font-semibold text-[#0F2922]">Auto-Approve (Trust Mode)</div>
                          <div className="text-xs text-slate-600">Donasi ≥ minimum langsung disetujui tanpa review manual.</div>
                        </div>
                      </div>
                      <Switch checked={!!settingsForm.auto_approve} onCheckedChange={v=>setS("auto_approve", v)} data-testid="settings-auto-toggle" />
                    </div>
                  </div>
                  <Button onClick={saveSettings} disabled={savingSettings} className="mt-5 w-full bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" data-testid="settings-save-btn">
                    {savingSettings ? "Menyimpan…" : "Simpan Pengaturan"}
                  </Button>
                  <p className="text-[11px] text-slate-500 mt-3">
                    * Metode pembayaran lain (QRIS, e-wallet) akan ditambahkan pada iterasi berikutnya. Untuk verifikasi bank yang lebih ketat, integrasikan Midtrans/Xendit dengan webhook.
                  </p>
                </div>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
